import Field from "@/components/Field";
import SubmitButton from "@/components/SubmitButton";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { CircleChevronLeft, RotateCcwKeyIcon } from "lucide-react";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormDataStep2 } from "../../types";
import PasswordPolicyChecklist from "../PasswordPolicyChecklist";
import ResetError from "../ResetError";
import VerificationCodeSentExplanation from "../VerificationCodeSentExplanation";
import schema from "./schema";

interface StepVerificationProps {
  onSubmit: (values: FormDataStep2) => Promise<void>;
  onSendAgain: () => void;
  email: string;
  sentTo?: string;
  errorCode?: string;
}

export default function StepVerification(props: StepVerificationProps) {
  const { t } = useTranslation();
  const initialValues = useRef<FormDataStep2>({
    code: "",
    password: "",
    passwordConfirmation: "",
  });

  const form = useForm({
    defaultValues: initialValues.current,
    resolver: zodResolver(schema),
    mode: "all",
  });

  const handleSubmit = async (values: FormDataStep2) => {
    try {
      await props.onSubmit(values);
    } catch (error) {
      const errorCode = error.code;
      switch (errorCode) {
        default:
          form.setError("password", {
            type: "manual",
            message: t("resetPassword.errorMessages.unknownError"),
          });
      }
    }
  };

  return (
    <Form {...form}>
      <form className="grid gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
        <VerificationCodeSentExplanation sentTo={props.sentTo} />
        <ResetError code={props.errorCode} />
        <input type="hidden" name="email" value={props.email} />
        <Field
          name="code"
          label={t("resetPassword.code")}
          render={({ field }) => <Input {...field} />}
        />

        <Field
          name="password"
          label={t("resetPassword.newPassword")}
          render={({ field }) => <Input {...field} type="password" />}
        />

        <Field
          name="passwordConfirmation"
          label={t("resetPassword.confirmPassword")}
          render={({ field }) => <Input {...field} type="password" />}
        />

        <PasswordPolicyChecklist />
        <footer className="flex place-content-between">
          <Button
            variant="link"
            className="pl-0"
            onClick={props.onSendAgain}
            type="button"
          >
            <CircleChevronLeft className="w-5 h-5 mr-1" />
            {t("resetPassword.sendAgain")}
          </Button>
          <SubmitButton
            label={t("resetPassword.resetPasswordConfirmButton")}
            loadingLabel={t("resetPassword.resettingPasswordConfirmButton")}
            icon={<RotateCcwKeyIcon />}
          />
        </footer>
      </form>
    </Form>
  );
}
